// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// CO-3: local-first editor preferences (functional core). Mirrors theme.ts's
// local-first approach, but for the editor-shaping prefs surfaced by the
// Settings dialog off the dashboard menu: the default canvas view, the editor
// dot-grid, and the "paper" tint. Storage is injected (Pick<Storage, ...>) so
// the parse/merge logic is unit-testable without a browser and degrades safely
// when storage is unavailable (private mode / blocked).
//
// Theme deliberately stays in theme.ts -- it is already wired app-wide via the
// `data-theme` attribute, so the Settings dialog composes the two rather than
// re-homing a working mechanism. These are per-device conveniences: no server
// schema change, consistent with the CO-3 "local-first" scope.

export type DefaultView = 'build' | 'loops' | 'play';
// Paper-tint options surfaced in Settings. Locked tints (Will 08-06): white
// (default) / very-light yellow / very-light green. The light/dark toggle is CO-3.
export type CanvasPaper = 'white' | 'yellow' | 'green';

export interface Preferences {
  /** Which canvas view the editor opens in. */
  defaultView: DefaultView;
  /** Whether the editor draws its dot-grid background. */
  canvasGrid: boolean;
  /** Canvas paper tint (light mode): white (default), very-light yellow, or green. */
  canvasPaper: CanvasPaper;
}

export const DEFAULT_PREFERENCES: Preferences = {
  defaultView: 'build',
  canvasGrid: false,
  canvasPaper: 'white',
};

const STORAGE_KEY = 'systems-analyzer.preferences';
const DEFAULT_VIEWS: readonly DefaultView[] = ['build', 'loops', 'play'];
const PAPERS: readonly CanvasPaper[] = ['white', 'yellow', 'green'];

type PrefStore = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * Pure: coerce untrusted stored JSON into a valid Preferences, filling any
 * missing or invalid field from the defaults. Never throws -- a corrupt or
 * partial blob (e.g. written by an older build) degrades field-by-field rather
 * than discarding the whole thing.
 */
export function parsePreferences(raw: string | null): Preferences {
  if (!raw) {
    return { ...DEFAULT_PREFERENCES };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return { ...DEFAULT_PREFERENCES };
  }
  const o = parsed as Record<string, unknown>;
  return {
    defaultView: DEFAULT_VIEWS.includes(o.defaultView as DefaultView)
      ? (o.defaultView as DefaultView)
      : DEFAULT_PREFERENCES.defaultView,
    canvasGrid: typeof o.canvasGrid === 'boolean' ? o.canvasGrid : DEFAULT_PREFERENCES.canvasGrid,
    canvasPaper: PAPERS.includes(o.canvasPaper as CanvasPaper)
      ? (o.canvasPaper as CanvasPaper)
      : DEFAULT_PREFERENCES.canvasPaper,
  };
}

/**
 * Load preferences from injected storage, falling back to defaults when storage
 * is missing or throws.
 */
export function loadPreferences(storage: PrefStore | undefined | null): Preferences {
  if (!storage) {
    return { ...DEFAULT_PREFERENCES };
  }
  try {
    return parsePreferences(storage.getItem(STORAGE_KEY));
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

/**
 * Persist preferences to injected storage. Best-effort: a storage failure is
 * swallowed (worst case the choice does not survive a reload).
 */
export function savePreferences(prefs: Preferences, storage: PrefStore | undefined | null): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore: private mode / quota / blocked storage
  }
}
